import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useActiveProfile } from "@/hooks/useActiveProfile";
import { usePlatformAccess, Platform } from "@/hooks/usePlatformAccess";
import { logDataSource } from "@/lib/dataSourceLogger";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Lock, Sparkles, AlertTriangle, CreditCard, RefreshCw } from "lucide-react";
import { useTrialStatus, TrialPlatform } from "@/hooks/useTrialStatus";

interface SubscriptionGateProps {
  platform: "callback_zone" | "coach" | "pro" | "castings" | "draftline" | "hmu" | "crew" | "capture";
  children: ReactNode;
}

const platformNames: Record<string, string> = {
  callback_zone: "Callback Zone",
  coach: "CastReady Coach",
  pro: "Partner Portal",
  castings: "CastReady Castings",
  draftline: "CastReady Draftline",
  hmu: "CastReady HMU",
  crew: "CastReady Crew",
  capture: "CastReady Capture",
};

const SubscriptionGate = ({ platform, children }: SubscriptionGateProps) => {
  const { user, hasRole } = useAuth();
  const { isDemoMode } = useDemo();
  const { effectiveUserId, isActingAsChild } = useActiveProfile();
  const { hasSubscription, getSubscription, isLoading, openCustomerPortal, checkSubscription } = useSubscription();
  const { hasPlatformAccess, isLoading: isPlatformAccessLoading, refetch: refetchPlatformAccess } = usePlatformAccess();
  const { trialStatus } = useTrialStatus();

  // Check if user is an admin - admins bypass subscription gates
  const isAdmin = hasRole("admin");
  const isParent = hasRole("parent");

  // Pre-compute access state so all hooks below can read it unconditionally.
  const hasDirectAccess = hasPlatformAccess(platform as Platform);
  const hasStripeAccess = hasSubscription(platform);
  const hasAccess = hasStripeAccess || hasDirectAccess;
  const subscription = getSubscription(platform);

  // Don't show "Subscription Required" until at least one fetch cycle has
  // completed. Without this guard, a race between the auth context loading
  // and the access fetches starting would render the paywall to entitled
  // users for a few hundred ms, then snap back to content.
  const [hasResolvedOnce, setHasResolvedOnce] = useState(false);
  const retryRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !isPlatformAccessLoading) {
      setHasResolvedOnce(true);
    }
  }, [isLoading, isPlatformAccessLoading]);

  // Log subscription check for audit trail
  useEffect(() => {
    if (!isLoading && effectiveUserId) {
      logDataSource(isDemoMode ? 'demo' : 'production', 'SubscriptionGate', {
        platform,
        effectiveUserId,
        isActingAsChild,
        hasAccess: hasSubscription(platform),
        isAdmin,
      });
    }
  }, [isLoading, effectiveUserId, platform, isActingAsChild, isDemoMode, hasSubscription, isAdmin]);

  // One-shot self-healing retry: if the gate is about to deny access but
  // we haven't actually completed a successful refresh yet (e.g. the edge
  // function was cold-starting), force one more fetch before showing the
  // paywall. Eliminates false-positive denials seen in production.
  useEffect(() => {
    if (
      hasResolvedOnce &&
      !hasAccess &&
      !retryRef.current &&
      user &&
      !isAdmin
    ) {
      retryRef.current = true;
      console.warn("[SubscriptionGate] Access denied — running one-shot refresh", {
        platform,
        effectiveUserId,
      });
      Promise.all([checkSubscription(true), refetchPlatformAccess()]).catch((e) =>
        console.error("[SubscriptionGate] retry failed", e),
      );
    }
  }, [
    hasResolvedOnce,
    hasAccess,
    user,
    isAdmin,
    platform,
    effectiveUserId,
    checkSubscription,
    refetchPlatformAccess,
  ]);

  // Early returns — all hooks above MUST run unconditionally first.
  if (isDemoMode) {
    return <>{children}</>;
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  // Parent free-access carve-out: parents managing their own dashboard view
  // (i.e. NOT acting on behalf of a child profile) never need a paid plan.
  if (isParent && !isActingAsChild && (platform === "callback_zone" || platform === "coach")) {
    return <>{children}</>;
  }

  if (isLoading || isPlatformAccessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check for grace period (past_due with grace_period_end in future)
  const isInGracePeriod = subscription?.status === "past_due";
  const gracePeriodEnd = subscription && 'grace_period_end' in subscription 
    ? (subscription as Record<string, unknown>).grace_period_end as string | null
    : null;
  const gracePeriodActive = isInGracePeriod && gracePeriodEnd && new Date(gracePeriodEnd) > new Date();

  // If in grace period, show warning banner but allow access
  if (gracePeriodActive) {
    const daysRemaining = Math.ceil(
      (new Date(gracePeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );

    return (
      <div className="min-h-screen flex flex-col">
        <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Payment Issue</AlertTitle>
          <AlertDescription className="flex items-center justify-between flex-wrap gap-2">
            <span>
              Your payment failed. Please update your payment method within {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} to avoid losing access.
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={() => openCustomerPortal()}
            >
              <CreditCard className="w-4 h-4" />
              Update Payment
            </Button>
          </AlertDescription>
        </Alert>
        {children}
      </div>
    );
  }

  if (!hasResolvedOnce) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    const handleRefreshAccess = async () => {
      try {
        await Promise.all([
          checkSubscription(true),
          refetchPlatformAccess(),
        ]);
      } catch (e) {
        console.error("[SubscriptionGate] Manual refresh failed", e);
      }
    };

    const trial = trialStatus?.[platform as TrialPlatform];
    const isLapsed = trial?.hadTrialBefore && !trial?.hasTrial;

    if (isLapsed) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Your free trial ended</CardTitle>
              <CardDescription>
                Reactivate your access to {platformNames[platform]} to keep your progress, lessons, and history.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Add a payment method to restore full access — your account stays exactly where you left off.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to={`/pricing#${platform}`}>
                  <Button className="w-full gap-2">
                    <CreditCard className="w-4 h-4" />
                    Reactivate {platformNames[platform]}
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full">
                    View All Plans
                  </Button>
                </Link>
                {user && (
                  <Button variant="ghost" className="w-full gap-2" onClick={handleRefreshAccess}>
                    <RefreshCw className="w-4 h-4" />
                    I already paid — refresh
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Subscription Required</CardTitle>
            <CardDescription>
              Access to {platformNames[platform]} requires an active subscription.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Unlock full access to lessons, castings, coaching tools, and more!
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {user && (
                <Button variant="secondary" className="w-full gap-2" onClick={handleRefreshAccess}>
                  <RefreshCw className="w-4 h-4" />
                  I should have access — refresh
                </Button>
              )}
              {(platform === "castings" || platform === "pro") && (
                // Literal href substrings ("topic=castings_pro" / "topic=pro") kept
                // intact so static smoke tests can grep for them.
                <Link to={platform === "castings" ? "/book-demo?topic=castings_pro" : "/book-demo?topic=pro"}>
                  <Button className="w-full gap-2">
                    <Sparkles className="w-4 h-4" />
                    Book a Demo
                  </Button>
                </Link>
              )}
              <Link to="/pricing">
                <Button variant={platform === "castings" || platform === "pro" ? "outline" : "default"} className="w-full gap-2">
                  <Sparkles className="w-4 h-4" />
                  View Subscription Plans
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Back to Home
                </Button>
              </Link>
            </div>
            {!user && (
              <p className="text-center text-sm text-muted-foreground">
                Already subscribed?{" "}
                <Link to="/auth" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default SubscriptionGate;
